/**
 * Invoice-domain validation path constants and helpers.
 *
 * The backend emits validation issues with dot-path identifiers tied to the
 * internal data model. The frontend (web-app + ui-toolkit) routes those
 * issues by inspecting the path. This module centralises the canonical
 * namespace so every consumer agrees on the same string literals — the
 * formatter, the modal form, the scroll-to-field hook, and the test
 * fixtures all import from here.
 */

import { BASE_ISSUE_PATH, parsePath } from './path';

/** Top-level section roots that appear as parent names in indexed paths. */
export const InvoiceSection = {
  invoiceLines: 'invoiceLines',
  vatSummary: 'vatSummary',
  bankingDetails: 'bankingDetails',
  invoiceTotals: 'invoiceTotals'
} as const;
export type InvoiceSection = (typeof InvoiceSection)[keyof typeof InvoiceSection];

/** Top-level scalar fields the UI reads via `validation.getField()`. */
export const InvoiceField = {
  invoiceNumber: 'invoiceNumber',
  invoiceDate: 'invoiceDate',
  dueDate: 'dueDate',
  taxDate: 'taxDate',
  description: 'description',
  paymentMethod: 'paymentMethod',
  paymentStatus: 'paymentStatus',
  paidAmount: 'paidAmount',
  accountingExchangeRate: 'accountingExchangeRate',
  vatSummaryExchangeRate: 'vatSummaryExchangeRate',
  totalAmountNoVat: 'totalAmountNoVat',
  totalVatAmount: 'totalVatAmount',
  totalAmountInclVat: 'totalAmountInclVat',
  roundingAmount: 'roundingAmount',
  supplierAddress: 'supplierAddress',
  documentType: 'documentType',
  originalDocumentReference: 'originalDocumentReference'
} as const;
export type InvoiceField = (typeof InvoiceField)[keyof typeof InvoiceField];

/** Fields nested under `bankingDetails.*`. */
export const BankingDetailField = {
  iban: 'iban',
  swift: 'swift',
  bankAccount: 'bankAccount',
  bankCode: 'bankCode',
  bankName: 'bankName',
  varSymb: 'varSymb'
} as const;
export type BankingDetailField = (typeof BankingDetailField)[keyof typeof BankingDetailField];

/** Fields on each invoice-line element (`invoiceLines.<N>.<field>`). */
export const InvoiceLineField = {
  description: 'description',
  lineNumber: 'lineNumber',
  quantity: 'quantity',
  unit: 'unit',
  unitPrice: 'unitPrice',
  unitPriceNoVat: 'unitPriceNoVat',
  unitPriceInclVat: 'unitPriceInclVat',
  discountPercent: 'discountPercent',
  recyclingFee: 'recyclingFee',
  vatRate: 'vatRate',
  vatAmount: 'vatAmount',
  totalAmountNoVat: 'totalAmountNoVat',
  totalAmountInclVat: 'totalAmountInclVat'
} as const;
export type InvoiceLineField = (typeof InvoiceLineField)[keyof typeof InvoiceLineField];

/** Fields on each VAT-summary element (`vatSummary.<N>.<field>`). */
export const VatSummaryField = {
  vatRate: 'vatRate',
  netAmount: 'netAmount',
  vatAmount: 'vatAmount'
} as const;
export type VatSummaryField = (typeof VatSummaryField)[keyof typeof VatSummaryField];

/**
 * Map from backend-emitted issue path → canonical frontend path. The backend
 * validation engine uses naming inherited from rule definitions (older
 * `lineItems` collection, isolated banking sub-validator that emits unqualified
 * field names) — this table collapses those variants onto the names the
 * frontend actually uses.
 */
const BACKEND_TO_FRONTEND_PATH: Readonly<Record<string, string>> = {
  number: InvoiceField.invoiceNumber,
  'supplier.address': InvoiceField.supplierAddress,
  lineItems: InvoiceSection.invoiceLines,
  variableSymbol: `${InvoiceSection.bankingDetails}.${BankingDetailField.varSymb}`,
  iban: `${InvoiceSection.bankingDetails}.${BankingDetailField.iban}`,
  swift: `${InvoiceSection.bankingDetails}.${BankingDetailField.swift}`,
  bankAccount: `${InvoiceSection.bankingDetails}.${BankingDetailField.bankAccount}`,
  bankCode: `${InvoiceSection.bankingDetails}.${BankingDetailField.bankCode}`
};

/**
 * Normalise a backend validation path to its canonical frontend equivalent.
 *
 * Performs three transformations in order:
 * 1. Strips the `invoice.` prefix produced by cross-validation rules.
 * 2. Collapses bracket-notation paths (`lineItems[...]`) to the section root.
 * 3. Applies the {@link BACKEND_TO_FRONTEND_PATH} rename table.
 *
 * Indexed paths within a known section (`invoiceLines.2.vatRate`) pass through
 * unchanged.
 *
 * @example
 * normalizeValidationPath('number')                       // 'invoiceNumber'
 * normalizeValidationPath('invoice.totalAmountInclVat')   // 'totalAmountInclVat'
 * normalizeValidationPath('invoiceLines.2.vatRate')       // 'invoiceLines.2.vatRate'
 */
export function normalizeValidationPath(backendPath: string | null | undefined): string {
  // BASE issues are not anchored to a field — empty path renders without a scroll link.
  if (!backendPath || backendPath === BASE_ISSUE_PATH) return '';
  if (backendPath.startsWith('invoice.')) {
    return backendPath.slice('invoice.'.length);
  }
  if (backendPath.startsWith('lineItems[')) {
    return InvoiceSection.invoiceLines;
  }
  return BACKEND_TO_FRONTEND_PATH[backendPath] ?? backendPath;
}

/**
 * Return the top-level section key (first dot-segment) of a normalised path.
 *
 * @example
 * fieldToSection('invoiceLines.3.vatRate') // 'invoiceLines'
 * fieldToSection('bankingDetails.iban')    // 'bankingDetails'
 * fieldToSection('invoiceDate')            // 'invoiceDate'
 */
export function fieldToSection(fieldKey: string): string {
  return parsePath(fieldKey)[0] ?? '';
}
